import { forwardRef, useState, useEffect, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import styles from './BudgetInput.module.scss';

export interface BudgetInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Label text */
  label?: string;
  /** Currency code (e.g. USD) */
  currency?: string;
  /** Budget type toggle */
  budgetType?: 'daily' | 'total';
  /** Budget type change handler */
  onBudgetTypeChange?: (type: 'daily' | 'total') => void;
  /** Value change handler returning cents */
  onChange?: (valueCents: number) => void;
  /** Error message */
  error?: string;
  /** Helper text shown below the input */
  helperText?: string;
  /** Additional class */
  className?: string;
}

export const BudgetInput = forwardRef<HTMLInputElement, BudgetInputProps>(function BudgetInput(
  {
    label = 'Budget',
    currency = 'USD',
    budgetType = 'daily',
    onBudgetTypeChange,
    onChange,
    error,
    helperText,
    className,
    value,
    ...props
  },
  ref,
) {
  // Local string state so the cursor doesn't jump while typing
  const [displayValue, setDisplayValue] = useState(() =>
    value != null && value !== '' ? String(value) : '',
  );

  // Sync from external value only when the input is NOT focused
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value != null && value !== '' ? String(value) : '');
    }
  }, [value, isFocused]);

  const emitCents = (raw: string) => {
    const dollars = parseFloat(raw) || 0;
    onChange?.(Math.round(dollars * 100));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
    emitCents(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Format on blur for clean display
    const dollars = parseFloat(e.target.value) || 0;
    if (dollars > 0) {
      setDisplayValue(dollars.toFixed(2));
    }
    props.onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  return (
    <div className={clsx(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      {onBudgetTypeChange && (
        <div className={styles.toggle}>
          <button
            type="button"
            className={clsx(styles.toggleOption, budgetType === 'daily' && styles.active)}
            onClick={() => onBudgetTypeChange('daily')}
          >
            Daily
          </button>
          <button
            type="button"
            className={clsx(styles.toggleOption, budgetType === 'total' && styles.active)}
            onClick={() => onBudgetTypeChange('total')}
          >
            Total
          </button>
        </div>
      )}

      <div className={clsx(styles.inputWrapper, error && styles.inputError)}>
        <span className={styles.currency}>{currency}</span>
        <input
          ref={ref}
          type="number"
          step="0.01"
          min="0"
          className={styles.input}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          aria-invalid={!!error}
          {...props}
        />
      </div>

      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
      {helperText && !error && <span className={styles.helper}>{helperText}</span>}
    </div>
  );
});
